<template>
    <div>
        <fragment>
            <el-form-item v-if="isMobile" class="text-center">
                <el-button type="primary" @click="scanning = true"><i class="fas fa-fw fa-barcode"></i> Scan Gift Card Barcode</el-button>
                <scanner format="code_128" :scanning.sync="scanning" append-to-body @scanned="checkBalance"></scanner>
            </el-form-item>
            <el-form-item label="Gift Card Number">
                <el-input v-model="number" clearable prefix-icon="el-icon-search" @keydown.native.enter="performSearch" @clear="performSearch">
                    <el-button slot="append" type="primary" :loading="searching" @click="performSearch">Search</el-button>
                </el-input>
                <p v-if="errorMessage" class="mt-2 font-bold text-red-500">Error: {{ errorMessage }}</p>
                <template v-if="giftCard !== null">
                    <p class="mt-2" :class="balanceClass">Balance available: {{ giftCard.balance | asCurrency }}</p>
                </template>
            </el-form-item>
        </fragment>
    </div>
</template>

<script>
    import axios from 'axios';
    import Dinero from 'dinero.js';
    import { Fragment } from 'vue-fragment';
    import asCurrency from '@/filters/asCurrency';
    import Scanner from '@/backend/components/Barcode/Scanner.vue';

    export default {
        name: 'GiftCard',
        components: {
            Fragment,
            Scanner,
        },
        filters: {
            asCurrency,
        },
        mixins: [],
        props: {
            value: {
                required: true,
                type: [Number, String],
            },
            payments: {
                required: true,
                type: Array,
            },
            canSubmit: {
                required: true,
                type: Boolean,
            },
            payment: {
                required: true,
                type: Object,
            },
        },
        data() {
            return {
                scanning: false,
                searching: false,
                number: '',
                giftCard: {},
                errorMessage: '',
            };
        },
        computed: {
            balanceClass() {
                const balance = new Dinero(this.giftCard.balance);

                if (balance.isPositive() && !balance.isZero()) {
                    return 'text-green-500';
                }

                return 'text-red-500';
            },
        },
        watch: {
            payment: {
                handler() {
                    if (new Dinero(this.giftCard.balance).greaterThanOrEqual(new Dinero(this.payment.amount))) {
                        this.errorMessage = '';
                        this.$emit('valid', {
                            fieldName: 'amount',
                            valid: true,
                            message: this.errorMessage,
                        });
                    } else {
                        this.errorMessage = 'Cannot tender more than the card has loaded.';
                        this.$emit('invalid', {
                            fieldName: 'amount',
                            valid: false,
                            message: this.errorMessage,
                        });
                    }
                },
                deep: true,
                immediate: true,
            },
            canSubmit(value) {
                if (value && this.giftCard !== null && !this.alreadyOnSale(this.value)) {
                    this.$emit('submit');
                }
            },
        },
        created() {
            this.number = this.value;
        },
        methods: {
            performSearch() {
                this.checkBalance(this.number);
            },
            alreadyOnSale(cardNumber) {
                return this.payments.findIndex((p) => p.card_number === cardNumber) !== -1;
            },
            checkBalance(cardNumber) {
                this.number = cardNumber;
                this.$emit('input', cardNumber);
                if (this.alreadyOnSale(cardNumber)) {
                    this.errorMessage = 'Gift Card Already On Sale';
                    this.$emit('invalid', {
                        fieldName: 'card_number',
                        valid: false,
                        message: this.errorMessage,
                    });
                } else if (cardNumber.length >= 10) {
                    axios.get(route('api.gift-cards.search', { giftCard: cardNumber })).then(
                        (response) => {
                            this.searching = false;
                            this.giftCard = response.data.data;
                            this.$emit('balance', response.data.data.balance);
                            const balance = new Dinero(response.data.data.balance);

                            if (!response.data.data.isActivated) {
                                this.errorMessage = 'Gift Card Is Not Activated';
                                this.$emit('invalid', {
                                    fieldName: 'card_number',
                                    valid: false,
                                    message: this.errorMessage,
                                });
                            } else if (balance.isPositive() && !balance.isZero()) {
                                if (balance.greaterThanOrEqual(new Dinero(this.payment.amount))) {
                                    this.errorMessage = '';
                                    this.$emit('valid', {
                                        fieldName: 'card_number',
                                        valid: true,
                                        message: this.errorMessage,
                                    });
                                } else {
                                    this.errorMessage = 'Cannot tender more than the card has loaded.';
                                    this.$emit('invalid', {
                                        fieldName: 'amount',
                                        valid: false,
                                        message: this.errorMessage,
                                    });
                                }
                            } else {
                                this.errorMessage = 'Gift Card Is Empty';
                                this.$emit('invalid', {
                                    fieldName: 'card_number',
                                    valid: false,
                                    message: this.errorMessage,
                                });
                            }
                        },
                        () => {
                            this.searching = false;
                            this.giftCard = null;
                            this.$emit('balance', { amount: 0, currency: 'USD' });
                            this.errorMessage = 'Gift Card Not Found';
                            this.$emit('invalid', {
                                fieldName: 'card_number',
                                valid: false,
                                message: this.errorMessage,
                            });
                        },
                    );
                } else {
                    this.errorMessage = 'Gift Card Number Too Short';
                    this.$emit('invalid', {
                        fieldName: 'card_number',
                        valid: false,
                        message: this.errorMessage,
                    });
                }
            },
        },
    };
</script>
